import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../lib/axios';
import type { OrganizationSubscription, AssignSubscriptionDto, UpdateSubscriptionDto, CancelSubscriptionDto } from '../../types/subscriptions';

const QUERY_KEYS = {
  orgSubscription: (orgId: string) => ['organizations', orgId, 'subscription'] as const,
};

export const useOrgSubscription = (orgId: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.orgSubscription(orgId),
    queryFn: async () => {
      const { data } = await axiosInstance.get<OrganizationSubscription>(
        `/subscriptions/organizations/${orgId}/subscription`
      );
      return data;
    },
    enabled: !!orgId,
  });
};

export const useAssignSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ organizationId, ...dto }: AssignSubscriptionDto) => {
      const { data } = await axiosInstance.post<OrganizationSubscription>(
        `/subscriptions/organizations/${organizationId}/subscription`,
        dto
      );
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.orgSubscription(variables.organizationId)
      });
    },
  });
};

export const useUpdateSubscription = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: UpdateSubscriptionDto) => {
      const { data } = await axiosInstance.patch<OrganizationSubscription>(
        `/subscriptions/organizations/${orgId}/subscription`,
        dto
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.orgSubscription(orgId) });
    },
  });
};

export const useCancelSubscription = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CancelSubscriptionDto) => {
      await axiosInstance.delete(`/subscriptions/organizations/${orgId}/subscription`, {
        data: dto,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.orgSubscription(orgId) });
    },
  });
};
